/**
 * ReportScheduleSettings - Configure scheduled/recurring reports
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  reportsApi,
  type ReportType,
  type ReportTypeInfo,
  type Project,
  type ScheduleReportRequest,
} from '@services/api';

interface ReportScheduleSettingsProps {
  organizationId: string;
  projects: Project[];
  reportTypes: ReportTypeInfo[];
  defaultReportType?: ReportType;
  defaultProjectId?: string;
  onClose?: () => void;
  compact?: boolean;
}

type FrequencyOption = 'daily' | 'weekly' | 'monthly';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: `${String(i).padStart(2, '0')}:00 UTC`,
}));

export function ReportScheduleSettings({
  organizationId,
  projects,
  reportTypes,
  defaultReportType = 'project_status',
  defaultProjectId = '',
  onClose,
  compact = false,
}: ReportScheduleSettingsProps) {
  const { t } = useTranslation();

  // Form state
  const [reportType, setReportType] = useState<ReportType>(defaultReportType);
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>(
    defaultProjectId ? [defaultProjectId] : []
  );
  const [frequency, setFrequency] = useState<FrequencyOption>('weekly');
  const [dayOfWeek, setDayOfWeek] = useState(0); // Monday
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [hour, setHour] = useState(9); // 9 AM UTC
  const [recipientEmails, setRecipientEmails] = useState('');
  const [includeAiSummary, setIncludeAiSummary] = useState(true);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleProjectToggle = (projectId: string) => {
    setSelectedProjectIds((prev) =>
      prev.includes(projectId)
        ? prev.filter((id) => id !== projectId)
        : [...prev, projectId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validate emails
    const emails = recipientEmails
      .split(/[,;\n]/)
      .map((email) => email.trim())
      .filter((email) => email.length > 0);

    if (emails.length === 0) {
      setError(t('reports.schedule.emailRequired'));
      return;
    }

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emails.filter((email) => !emailRegex.test(email));
    if (invalidEmails.length > 0) {
      setError(t('reports.schedule.invalidEmails', { emails: invalidEmails.join(', ') }));
      return;
    }

    setIsSubmitting(true);

    try {
      const request: ScheduleReportRequest = {
        reportType,
        organizationId,
        projectIds: selectedProjectIds.length > 0 ? selectedProjectIds : undefined,
        frequency,
        recipientEmails: emails,
        includeAiSummary,
        dayOfWeek: frequency === 'weekly' ? dayOfWeek : undefined,
        dayOfMonth: frequency === 'monthly' ? dayOfMonth : undefined,
        hour,
        enabled: true,
      };

      const response = await reportsApi.scheduleReport(request);
      setSuccess(response.message);

      // Clear form on success
      if (onClose) {
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to schedule report:', err);
      setError(t('reports.schedule.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const containerClass = compact
    ? ''
    : 'bg-white dark:bg-neutral-800 rounded-xl shadow-sm border border-neutral-200 dark:border-neutral-700 p-6';

  return (
    <div className={containerClass}>
      {!compact && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
            {t('reports.schedule.title')}
          </h2>
          <p className="text-sm text-neutral-500 mt-1">
            {t('reports.schedule.subtitle')}
          </p>
        </div>
      )}

      {compact && (
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
          {t('reports.schedule.title')}
        </h3>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Report Type */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
            {t('reports.reportType')}
          </label>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value as ReportType)}
            className="w-full px-4 py-2.5 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {reportTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>

        {/* Projects */}
        {projects.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              {t('reports.schedule.projects')}
              <span className="text-neutral-400 font-normal ml-1">
                ({t('reports.schedule.optional')})
              </span>
            </label>
            <div className="max-h-40 overflow-y-auto border border-neutral-200 dark:border-neutral-700 rounded-lg p-2 space-y-1">
              {projects.map((project) => (
                <label
                  key={project.id}
                  className="flex items-center gap-2 p-2 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 rounded cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedProjectIds.includes(project.id)}
                    onChange={() => handleProjectToggle(project.id)}
                    className="w-4 h-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm text-neutral-700 dark:text-neutral-300">
                    {project.name}
                  </span>
                  <span className="text-xs text-neutral-400">{project.key}</span>
                </label>
              ))}
            </div>
            {selectedProjectIds.length === 0 && (
              <p className="text-xs text-neutral-500 mt-1">
                {t('reports.schedule.allProjectsHint')}
              </p>
            )}
          </div>
        )}

        {/* Frequency */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
            {t('reports.schedule.frequency')}
          </label>
          <div className="grid grid-cols-3 gap-2">
            {(['daily', 'weekly', 'monthly'] as const).map((freq) => (
              <button
                key={freq}
                type="button"
                onClick={() => setFrequency(freq)}
                className={`px-4 py-2.5 text-sm font-medium rounded-lg border transition-colors ${
                  frequency === freq
                    ? 'bg-primary-50 dark:bg-primary-900/30 border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-300'
                    : 'bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800'
                }`}
              >
                {t(`reports.schedule.${freq}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Day of Week (for weekly) */}
        {frequency === 'weekly' && (
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              {t('reports.schedule.dayOfWeek')}
            </label>
            <select
              value={dayOfWeek}
              onChange={(e) => setDayOfWeek(Number(e.target.value))}
              className="w-full px-4 py-2.5 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {DAYS_OF_WEEK.map((day) => (
                <option key={day.value} value={day.value}>
                  {day.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Day of Month (for monthly) */}
        {frequency === 'monthly' && (
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              {t('reports.schedule.dayOfMonth')}
            </label>
            <select
              value={dayOfMonth}
              onChange={(e) => setDayOfMonth(Number(e.target.value))}
              className="w-full px-4 py-2.5 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                <option key={day} value={day}>
                  {day}
                </option>
              ))}
            </select>
            <p className="text-xs text-neutral-500 mt-1">
              {t('reports.schedule.dayOfMonthHint')}
            </p>
          </div>
        )}

        {/* Time */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
            {t('reports.schedule.time')}
          </label>
          <select
            value={hour}
            onChange={(e) => setHour(Number(e.target.value))}
            className="w-full px-4 py-2.5 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {HOURS.map((h) => (
              <option key={h.value} value={h.value}>
                {h.label}
              </option>
            ))}
          </select>
        </div>

        {/* Recipients */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
            {t('reports.schedule.recipients')}
          </label>
          <textarea
            value={recipientEmails}
            onChange={(e) => setRecipientEmails(e.target.value)}
            placeholder={t('reports.schedule.recipientsPlaceholder')}
            rows={3}
            className="w-full px-4 py-2.5 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
          />
          <p className="text-xs text-neutral-500 mt-1">
            {t('reports.schedule.recipientsHint')}
          </p>
        </div>

        {/* AI Summary Toggle */}
        <div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={includeAiSummary}
              onChange={(e) => setIncludeAiSummary(e.target.checked)}
              className="w-4 h-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
            />
            <div>
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {t('reports.includeAiSummary')}
              </span>
              <p className="text-xs text-neutral-500">
                {t('reports.aiSummaryDescription')}
              </p>
            </div>
          </label>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {success && (
          <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <p className="text-sm text-green-600 dark:text-green-400 flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {success}
            </p>
          </div>
        )}

        {/* Submit Button */}
        <div className={compact ? 'flex justify-end gap-3' : ''}>
          {compact && onClose && (
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
            >
              {t('common.cancel')}
            </button>
          )}
          <button
            type="submit"
            disabled={isSubmitting}
            className={`flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
              compact ? '' : 'w-full'
            }`}
          >
            {isSubmitting ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                {t('reports.schedule.scheduling')}
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                {t('reports.schedule.createSchedule')}
              </>
            )}
          </button>
        </div>
      </form>

      {/* Preview of Schedule */}
      {!compact && (
        <div className="mt-6 pt-6 border-t border-neutral-200 dark:border-neutral-700">
          <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
            {t('reports.schedule.preview')}
          </h3>
          <div className="bg-neutral-50 dark:bg-neutral-900/50 rounded-lg p-4">
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              {getSchedulePreview(frequency, dayOfWeek, dayOfMonth, hour)}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function getSchedulePreview(
  frequency: FrequencyOption,
  dayOfWeek: number,
  dayOfMonth: number,
  hour: number
): string {
  const hourStr = `${String(hour).padStart(2, '0')}:00 UTC`;

  switch (frequency) {
    case 'daily':
      return `Report will be sent every day at ${hourStr}`;
    case 'weekly':
      return `Report will be sent every ${DAYS_OF_WEEK[dayOfWeek]?.label || 'Monday'} at ${hourStr}`;
    case 'monthly':
      return `Report will be sent on the ${dayOfMonth}${getOrdinalSuffix(dayOfMonth)} of every month at ${hourStr}`;
    default:
      return '';
  }
}

function getOrdinalSuffix(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0] || '';
}

export default ReportScheduleSettings;
